import { useRouter } from "next/router";
import { DM_Sans } from "next/font/google";
import { PublicProfile } from "@dimensional-engineering/dimensional-models";

import styles from "./PublicProfileHeader.module.css";

import SearchBarPublicProfiles from "../Header/SearchBarPublicProfiles";
import {
  ExtremelyLargeProfileImage,
  VeryLargeProfileImage,
} from "@/components/shared/SmallProfileImage/LargeProfileImage";
import SocialLinksPublicProfile from "../SocialLinks/SocialLinksPublicProfile";
import { usePublicProfileContext } from "@/context/PublicProfileContext";
import AmbassadorIcon from "@/components/shared/icons/AmbassadorIcon";
import BackButton from "@/components/shared/buttons/BackButton";
import { Mixpanel } from "@/helpers/mixpanel";

const font = DM_Sans({ weight: "500", subsets: ["latin"] });

export default function PublicProfileHeader(props: {
  publicProfile: PublicProfile;
  imageUrl: string;
  onOpenConnectionList: () => void;
}) {
  const { setModal } = usePublicProfileContext();

  const router = useRouter();

  return (
    <section className={styles.main}>
      <div className={styles.header}>
        {typeof window !== "undefined" && window.Android && (
          <div onClick={() => router.back()} className={styles.backButton}>
            <BackButton />
          </div>
        )}
        <div
          style={
            typeof window !== "undefined" && window.Android
              ? { marginLeft: "40px" }
              : {}
          }
          className={styles.headerLeft}
        >
          <Logo />
          <h3 className={`${styles.dimensionalTitle} ${font.className}`}>
            Dimensional
          </h3>
          <h5 className={styles.subtitle}>Know yourself (and others)</h5>
        </div>
        <div className={styles.headerRight}>
          <SearchBarPublicProfiles />
        </div>
      </div>
      <div className={styles.bodyDiv}>
        <div
          onClick={() => props.onOpenConnectionList()}
          className={styles.connectionsDiv}
        >
          <p className={styles.connectionsCount}>
            {props.publicProfile.numFriends}
          </p>
          <p className={styles.connectionText}>Connections</p>
        </div>
        {props.publicProfile.isAmbassador && (
          <div className={styles.ambassDiv}>
            <AmbassadorIcon />
            <p className={styles.connectionText}>Ambassador</p>
          </div>
        )}
        <div></div>
        <div className={styles.desktopImage}>
          <ExtremelyLargeProfileImage
            publicProfile={props.publicProfile}
            imageUrl={props.imageUrl}
            noLink
          />
        </div>
        <div className={styles.mobileImage}>
          <VeryLargeProfileImage
            publicProfile={props.publicProfile}
            imageUrl={props.imageUrl}
            noLink
          />
        </div>
        <h1 className={styles.name}>
          {props.publicProfile.name ?? `@${props.publicProfile.userName}`}
        </h1>
        <div className={styles.scoialsDiv}>
          <SocialLinksPublicProfile
            onClick={(type) => {
              Mixpanel?.track(
                "Profile social link clicked",
                {
                  type: type,
                  profile_username: props.publicProfile.userName,
                },
                "d"
              );
            }}
            data={props.publicProfile.socialMediaMap}
          />
        </div>
        <button
          className={styles.viewProfileButton}
          onClick={() => {
            Mixpanel?.track(
              "View full profile CTA clicked",
              {
                profile_username: props.publicProfile.userName,
              },
              "d"
            );
            setModal(true);
          }}
        >
          View full profile on Dimensional
        </button>
      </div>
    </section>
  );
}

function Logo() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="91"
      height="27"
      viewBox="0 0 91 27"
      fill="none"
      className={styles.logo}
    >
      <rect
        x="31.4727"
        width="7.77555"
        height="26.659"
        rx="3.88778"
        fill="#FFFFF2"
      />
      <rect
        x="40.875"
        width="7.77555"
        height="26.659"
        rx="3.88778"
        fill="#FFFFF2"
      />
      <rect
        x="50.3398"
        width="7.77555"
        height="26.659"
        rx="3.88778"
        fill="#FFFFF2"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.3295 26.659C20.6912 26.659 26.659 20.6912 26.659 13.3295C26.659 5.96783 20.6912 0 13.3295 0C5.96783 0 0 5.96783 0 13.3295C0 20.6912 5.96783 26.659 13.3295 26.659ZM13.2852 15.4895C14.503 15.4895 15.4902 14.5023 15.4902 13.2844C15.4902 12.0666 14.503 11.0793 13.2852 11.0793C12.0673 11.0793 11.0801 12.0666 11.0801 13.2844C11.0801 14.5023 12.0673 15.4895 13.2852 15.4895Z"
        fill="#FFFFF2"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M87.5139 26.0308C90.2077 26.0308 91.88 23.1015 90.5107 20.7815L79.636 2.35665C78.2894 0.0751268 74.989 0.0751295 73.6424 2.35665L62.7677 20.7815C61.3984 23.1015 63.0707 26.0308 65.7645 26.0308H87.5139ZM76.6629 18.0447C77.9122 18.0447 78.9249 17.0319 78.9249 15.7825C78.9249 14.5332 77.9122 13.5204 76.6629 13.5204C75.4137 13.5204 74.401 14.5332 74.401 15.7825C74.401 17.0319 75.4137 18.0447 76.6629 18.0447Z"
        fill="#FFFFF2"
      />
    </svg>
  );
}
